import React, {useEffect, useMemo, useState} from 'react'
import {toAbsoluteUrl} from '../../../_metronic/helpers'

export type AvailableVehicleGridRow = {
  regNo?: string | null
  jobCount?: number | null
  totalKM?: number | null
  TotalKM?: number | null
  revenue?: number | null
  Revenue?: number | null
}

export type SortKey = 'regNo' | 'jobCount' | 'totalKM' | 'revenue'
export type SortDir = 'asc' | 'desc'

type Props = {
  rows: AvailableVehicleGridRow[]
  totalCount: number
  page: number
  pageSize: number
  search: string
  sortKey: SortKey
  sortDir: SortDir
  allTime: boolean
  isLoading?: boolean
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
  onSearchChange: (search: string) => void
  onSortChange: (key: SortKey, dir: SortDir) => void
  onAllTimeChange: (allTime: boolean) => void
}

const PAGE_SIZES = [10, 25, 50, 100] as const

function toNumber(value: unknown): number | null {
  if (value == null || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

function getTotalKm(row: AvailableVehicleGridRow): number | null {
  return toNumber(row.totalKM ?? row.TotalKM)
}

function getRevenue(row: AvailableVehicleGridRow): number | null {
  return toNumber(row.revenue ?? row.Revenue)
}

function formatInt(value: number | null): string {
  if (value == null) return '—'
  return Math.round(value).toLocaleString('en-US')
}

function formatRevenue(value: number | null): string {
  if (value == null) return '—'
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

function buildPageWindow(current: number, total: number): number[] {
  if (total <= 7) {
    return Array.from({length: total}, (_, i) => i + 1)
  }
  const pages = new Set<number>([1, total, current, current - 1, current + 1, current - 2, current + 2])
  return Array.from(pages)
    .filter((p) => p >= 1 && p <= total)
    .sort((a, b) => a - b)
}

function currentMonthLabel(now = new Date()): string {
  return now.toLocaleString('en-US', {month: 'long', year: 'numeric'})
}

export const AvailableVehicleGrid: React.FC<Props> = ({
  rows,
  totalCount,
  page,
  pageSize,
  search,
  sortKey,
  sortDir,
  allTime,
  isLoading,
  onPageChange,
  onPageSizeChange,
  onSearchChange,
  onSortChange,
  onAllTimeChange,
}) => {
  const [searchInput, setSearchInput] = useState(search)

  useEffect(() => {
    setSearchInput(search)
  }, [search])

  useEffect(() => {
    const handle = window.setTimeout(() => {
      if (searchInput !== search) {
        onSearchChange(searchInput)
      }
    }, 350)
    return () => window.clearTimeout(handle)
  }, [searchInput, search, onSearchChange])

  const total = totalCount
  const totalPages = Math.max(1, Math.ceil(Math.max(0, total) / pageSize) || 1)
  const hasRows = (rows || []).length > 0
  const showEmpty = !isLoading && total === 0 && !hasRows
  const showFooter = total > 0 || (isLoading && (total > 0 || hasRows))
  const rangeLabel = allTime ? 'All time' : `Current month · ${currentMonthLabel()}`

  useEffect(() => {
    if (page > totalPages) onPageChange(totalPages)
  }, [page, totalPages, onPageChange])

  const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1
  const rangeEnd = total === 0 ? 0 : Math.min(page * pageSize, total)
  const pageWindow = useMemo(() => buildPageWindow(page, totalPages), [page, totalPages])

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      onSortChange(key, sortDir === 'asc' ? 'desc' : 'asc')
    } else {
      onSortChange(key, key === 'regNo' ? 'asc' : 'desc')
    }
  }

  const sortClass = (key: SortKey) =>
    `sortable ${sortKey === key ? `is-sorted is-sorted--${sortDir}` : ''}`

  return (
    <div className='av-grid h-100 d-flex flex-column'>
      <div className='av-grid-toolbar'>
        <label className='av-grid-toolbar__alltime form-check form-check-custom form-check-solid form-check-sm mb-0'>
          <input
            className='form-check-input'
            type='checkbox'
            checked={allTime}
            onChange={(e) => onAllTimeChange(e.target.checked)}
            id='av-grid-all-time'
          />
          <span className='form-check-label text-gray-700 fs-7 ms-2'>All time</span>
        </label>

        <div className='av-grid-toolbar__range' title={rangeLabel}>
          {rangeLabel}
        </div>

        <div className='av-grid-toolbar__controls'>
          <input
            type='text'
            className='form-control form-control-sm av-grid-search'
            placeholder='Search plate'
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <select
            className='form-select form-select-sm av-grid-page-size'
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            aria-label='Rows per page'
          >
            {PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {size} / page
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className='av-grid-table-wrap flex-grow-1'>
        {showEmpty ? (
          <div className='d-flex text-center justify-content-center flex-column align-items-center h-100 py-10'>
            <img
              alt='Empty'
              src={toAbsoluteUrl('/media/other/empty.png')}
              className='h-auto w-50px'
              style={{opacity: 0.5}}
            />
            <h3 className='text-gray-400 fs-7 mt-3 fw-normal mb-0'>No records found</h3>
          </div>
        ) : (
          <table className='table table-row-dashed gy-2 gs-5 mb-0 av-grid-table'>
            <thead className='sticky-header'>
              <tr className='fw-bold fs-6 text-white border-bottom border-gray-200 bg-dark gy-5'>
                <th className={sortClass('regNo')}>
                  <button type='button' className='av-grid-sort-btn' onClick={() => toggleSort('regNo')}>
                    Vehicle Number
                  </button>
                </th>
                <th className={`text-end ${sortClass('jobCount')}`}>
                  <button
                    type='button'
                    className='av-grid-sort-btn'
                    onClick={() => toggleSort('jobCount')}
                  >
                    Job Count
                  </button>
                </th>
                <th className={`text-end ${sortClass('totalKM')}`}>
                  <button
                    type='button'
                    className='av-grid-sort-btn'
                    onClick={() => toggleSort('totalKM')}
                  >
                    Total KM
                  </button>
                </th>
                <th className={`text-end ${sortClass('revenue')}`}>
                  <button
                    type='button'
                    className='av-grid-sort-btn'
                    onClick={() => toggleSort('revenue')}
                  >
                    Revenue (LKR)
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {(rows || []).map((row, index) => (
                <tr key={`${row?.regNo ?? 'row'}-${(page - 1) * pageSize + index}`}>
                  <td>{row?.regNo ?? '—'}</td>
                  <td className='num'>{formatInt(toNumber(row.jobCount))}</td>
                  <td className='num'>{formatInt(getTotalKm(row))}</td>
                  <td className='num'>{formatRevenue(getRevenue(row))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {isLoading ? (
          <div className='av-grid-loading-overlay' aria-busy='true' aria-live='polite'>
            <div className='spinner-border text-primary' role='status'>
              <span className='visually-hidden'>Loading…</span>
            </div>
          </div>
        ) : null}
      </div>

      {showFooter ? (
        <div className='av-grid-pagination'>
          <span className='av-grid-pagination__count'>
            Showing {rangeStart}–{rangeEnd} of {total}
          </span>
          <div className='av-grid-pagination__controls'>
            <button
              type='button'
              className='btn btn-sm btn-light'
              disabled={page <= 1 || isLoading}
              onClick={() => onPageChange(Math.max(1, page - 1))}
            >
              Prev
            </button>
            <div className='av-grid-pagination__pages'>
              {pageWindow.map((p, idx) => {
                const prev = pageWindow[idx - 1]
                const showEllipsis = prev != null && p - prev > 1
                return (
                  <React.Fragment key={p}>
                    {showEllipsis ? <span className='av-grid-pagination__ellipsis'>…</span> : null}
                    <button
                      type='button'
                      className={`btn btn-sm ${p === page ? 'btn-primary' : 'btn-light'}`}
                      disabled={isLoading}
                      onClick={() => onPageChange(p)}
                    >
                      {p}
                    </button>
                  </React.Fragment>
                )
              })}
            </div>
            <button
              type='button'
              className='btn btn-sm btn-light'
              disabled={page >= totalPages || isLoading}
              onClick={() => onPageChange(Math.min(totalPages, page + 1))}
            >
              Next
            </button>
          </div>
        </div>
      ) : null}
    </div>
  )
}
